import React from "react";
import {IonButton} from "@ionic/react";
import {Booking, Company, ProfilePackage, Slot} from "../../models";
import BookingAddToCalendarButton from "../bookings/BookingAddToCalendarButton";

interface SlotButtonsProps {
    company: Company;
    slot: Slot;
    profilePackage: ProfilePackage;
    visibleBookingIds: {[id: number]: number};
    visibleBookings?: Booking[];
    visibleWaitingBookingIds: {[id: number]: number};
    actionsDisabled: boolean;
    slotDescription: string;
    onBookClicked: (slotId: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
    onDeleteClicked: (bookingId: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
    onCancelClicked: (bookingId: number, lessonDateTs: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
    onUncancelClicked: (bookingId: number, lessonDateTs: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
    onWaitClicked: (slotId: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
    onCancelWaitClicked: (waitingBookingId: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
    onMoveClicked?: (bookingId: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
    deleteText?: string;
    cancelText?: string;
    uncancelText?: string;
    bookText?: string;
    waitText?: string;
    waitCancelText?: string;
    moveText?: string;
    moveMode?: boolean;

    expand?: 'full' | 'block';
    fill?: 'clear' | 'outline' | 'solid' | 'default';
}

const SlotButtons: React.FC<SlotButtonsProps> = ({
                                                     company,
                                                     slot,
                                                     profilePackage,
                                                     visibleBookings,
                                                     visibleBookingIds,
                                                     visibleWaitingBookingIds,
                                                     actionsDisabled,
                                                     slotDescription,
                                                     onCancelClicked,
                                                     onUncancelClicked,
                                                     onDeleteClicked,
                                                     onCancelWaitClicked,
                                                     onWaitClicked,
                                                     onBookClicked,
                                                     onMoveClicked,
                                                     deleteText = 'Cancel',
                                                     cancelText = 'Cancel',
                                                     uncancelText = 'Uncancel',
                                                     bookText = 'Book',
                                                     waitText = 'Waiting List',
                                                     waitCancelText = 'Cancel Waiting',
                                                     moveText = 'Move',
                                                     moveMode = false,
                                                     expand,
                                                     fill,
                                                 }) => {
    const isBooked = slot.isBooked;
    const isWaitingBooked = slot.isWaitingBooked;
    // Normal mode's target is "book this slot" (canBook); move mode's target
    // is "move the in-flight booking here" (canMoveTo) — the server composes
    // canMoveTo like canBook minus the package's weekly-limit leg, since a
    // move is limit-neutral. Using canBook in move mode is the WI-6 bug: a
    // learner at their booking_limit has canBook:false everywhere, which
    // silently hid every valid move destination.
    const canBook = moveMode ? slot.canMoveTo : slot.canBook;
    const canDelete = slot.canDelete;
    const canWait = slot.canWait;
    const canCancelWait = slot.canCancelWait;
    const isRecurring = profilePackage?.isRecurring;
    const loadedBookingId = visibleBookingIds[slot.id];
    const loadedWaitingBookingId = visibleWaitingBookingIds[slot.id];
    const loadedBooking: Booking|undefined = (visibleBookings && loadedBookingId) ? visibleBookings.find((b) => b.id === loadedBookingId) : undefined;

    // While a move is in progress, the ONLY live actions anywhere on the page
    // are "Move Here" (bookBtn, relabelled by the container) and the banner's
    // Cancel Move — blanket-suppressed rather than special-casing just the
    // moving slot, so no other action can leave the move banner dangling.
    const cancelBtn = isBooked && isRecurring && slot.canCancel && !!loadedBookingId && !moveMode && (
        <IonButton
            key={`slot-${slot.id}-cancel`}
            disabled={actionsDisabled}
            color={"warning"}
            expand={expand}
            fill={fill}
            onClick={(event) =>
                onCancelClicked(loadedBookingId, slot.lessonDateTs, slotDescription, event)
            }
        >
            {cancelText}
        </IonButton>
    );

    // A cancelled-but-not-made-up future occurrence: the booking itself is
    // still live (isBooked stays true — only that date's attendance is
    // cancelled), so this sits alongside cancelBtn, never with it (the API's
    // can_cancel/can_uncancel legs are mutually exclusive on is_cancelled).
    const uncancelBtn = isBooked && isRecurring && slot.canUncancel && !!loadedBookingId && !moveMode && (
        <IonButton
            key={`slot-${slot.id}-uncancel`}
            disabled={actionsDisabled}
            color={"primary"}
            expand={expand}
            fill={fill}
            onClick={(event) =>
                onUncancelClicked(loadedBookingId, slot.lessonDateTs, slotDescription, event)
            }
        >
            {uncancelText}
        </IonButton>
    );

    const deleteBtn = isBooked && !isRecurring && canDelete && !!loadedBookingId && !moveMode && (
        <IonButton
            key={`slot-${slot.id}-delete`}
            disabled={actionsDisabled}
            color={"warning"}
            expand={expand}
            fill={fill}
            onClick={(event) => onDeleteClicked(loadedBookingId, slotDescription, event)}
        >
            {deleteText}
        </IonButton>
    );

    const moveBtn = isBooked && slot.canMove && !!loadedBookingId && !moveMode && onMoveClicked && (
        <IonButton
            key={`slot-${slot.id}-move`}
            disabled={actionsDisabled}
            color={"primary"}
            expand={expand}
            fill={fill}
            onClick={(event) => onMoveClicked(loadedBookingId, slotDescription, event)}
        >
            {moveText}
        </IonButton>
    );

    const bookBtn = canBook && !isBooked && !loadedBookingId && !!slot.availableCount && (
        <IonButton
            key={`slot-${slot.id}-book`}
            disabled={actionsDisabled}
            color={"primary"}
            expand={expand}
            fill={fill}
            onClick={(event) => onBookClicked(slot.id, slotDescription, event)}
        >
            {bookText}
        </IonButton>
    );

    const waitBtn = canWait && !isBooked && !isWaitingBooked && !loadedWaitingBookingId && !moveMode && (
        <IonButton
            key={`slot-${slot.id}-wait`}
            disabled={actionsDisabled}
            color={"primary"}
            expand={expand}
            fill={fill}
            onClick={(event) => onWaitClicked(slot.id, slotDescription, event)}
        >
            {waitText}
        </IonButton>
    );

    const cancelWaitBtn = canCancelWait && isWaitingBooked && !isBooked && !!loadedWaitingBookingId && !moveMode && (
        <IonButton
            key={`slot-${slot.id}-cancel-wait`}
            disabled={actionsDisabled}
            color={"warning"}
            expand={expand}
            fill={fill}
            onClick={(event) => onCancelWaitClicked(loadedWaitingBookingId, slotDescription, event)}
        >
            {waitCancelText}
        </IonButton>
    );

    const addToCalendarBtn = isBooked && !isRecurring && canDelete && !!loadedBookingId && !!loadedBooking && (
        <BookingAddToCalendarButton
            company={company}
            booking={loadedBooking}
            profilePackage={profilePackage}
            key={`slot-${slot.id}-add-to-calendar`}
            actionsDisabled={actionsDisabled}
            expand={expand}
            fill={fill}
        />
    );

    if (!profilePackage || (!cancelBtn && !uncancelBtn && !deleteBtn && !bookBtn && !waitBtn && !cancelWaitBtn && !moveBtn)){
        return null;
    }
    return (
        <>
            {addToCalendarBtn}
            {cancelBtn}
            {uncancelBtn}
            {deleteBtn}
            {moveBtn}
            {bookBtn}
            {waitBtn}
            {cancelWaitBtn}
        </>
    );
};

export default SlotButtons;
